import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import companyData from "const/companyData.json";

export const PAYMENT_STATUS = {
  UNPAID: "UNPAID",
  PARTIALLY_PAID: "PARTIALLY_PAID",
  PAID: "PAID",
};

export const DASHBOARD_UNPAID_INVOICES_QUERY_KEY = ["dashboard-unpaid-invoices"];
export const DASHBOARD_UNPAID_INVOICES_PAGE_SIZE = 10;

export const fetchDashboardUnpaidInvoices = async () => {
  const res = await axios.get(`invoice`, {
    params: {
      page: 0,
      size: DASHBOARD_UNPAID_INVOICES_PAGE_SIZE,
      paymentStatus: PAYMENT_STATUS.UNPAID,
      sortBy: "issueDate",
      sortDirection: "DESC",
    },
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });
  return res?.data?.data;
};

export const useDashboardUnpaidInvoices = (options = {}) =>
  useQuery({
    queryKey: DASHBOARD_UNPAID_INVOICES_QUERY_KEY,
    queryFn: fetchDashboardUnpaidInvoices,
    refetchOnWindowFocus: false,
    ...options,
  });

export const getPaymentStatus = (invoice) =>
  invoice?.paymentStatus ||
  (invoice?.status === true ? PAYMENT_STATUS.PAID : PAYMENT_STATUS.UNPAID);

export const getInvoiceRows = (item) => item?.invoiceItems || item?.invoices || [];

export const getInvoiceRowAmount = (item) => Number(item?.total ?? item?.price ?? 0) || 0;

export const getTotalGST = (item) => {
  const total = getInvoiceRows(item)
    ?.filter((inv) => inv?.gst === "yes" || inv?.gst === "true" || inv?.gst === true)
    ?.reduce((sum, inv) => sum + Number(inv?.total) * 0.1, 0);
  return total || 0;
};

export const getInvoiceGrossAmount = (item) => Number(item?.subTotal || 0) + getTotalGST(item);

const hasItemPaymentStatus = (item) =>
  getInvoiceRows(item).some((row) => typeof row?.status === "boolean");

export const getInvoiceTotalAmount = (item) => {
  const rows = getInvoiceRows(item);
  if (rows.length && hasItemPaymentStatus(item)) {
    return rows.reduce((sum, row) => sum + getInvoiceRowAmount(row), 0);
  }
  return getInvoiceGrossAmount(item);
};

export const getInvoiceUnpaidAmount = (item) => {
  if (item?.lifecycleStatus === "DRAFT" || item?.lifecycleStatus === "PENDING_ACTIVATION") return 0;
  const status = getPaymentStatus(item);
  if (status === PAYMENT_STATUS.PARTIALLY_PAID) {
    return Math.max(getInvoiceTotalAmount(item) - Number(item?.partialPaidAmount || 0), 0);
  }
  if (status === PAYMENT_STATUS.PAID) {
    return 0;
  }
  if (hasItemPaymentStatus(item)) {
    return getInvoiceRows(item)
      .filter((row) => row?.status !== true)
      .reduce((sum, row) => sum + getInvoiceRowAmount(row), 0);
  }
  return item?.status === false ? getInvoiceGrossAmount(item) : 0;
};

/**
 * The number an invoice is quoted by. Nothing stores it: it is the issuing company's initials, the
 * literal INV, and the invoice's own id.
 *
 * Invoices raised before the company was recorded against them carry no companyName, and reading
 * that gap literally produced INV-INV-42 in the list while the edit screen, which fills the same
 * gap from the default company, showed KGPL-INV-42 for the very same invoice. Both fall back the
 * same way here so one invoice has one number wherever it is shown.
 */
export const getInvoiceNumber = (item) => {
  const company =
    item?.companyName || item?.invoiceFrom?.companyName || companyData?.[0]?.name || "";
  const prefix = company
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0].toUpperCase())
    .join("");
  return `${prefix || "INV"}-INV-${item?.invoiceId}`;
};

/**
 * Whether a payment row counts toward what has actually been received. Only VERIFIED money counts, and a row
 * with no status at all is a legacy or imported payment, which the backend counts too.
 */
export const countsTowardPaid = (payment) => {
  const status = payment?.status;
  if (status === "VERIFIED") return true;
  if (status === "PENDING_VERIFICATION" || status === "REJECTED" || status === "NONE") return false;
  return status == null || status === "";
};

/** A refund row is money given back, so it counts against what has been received. */
const signedPaymentAmount = (payment) =>
  (payment?.entryType === "REFUND" ? -1 : 1) * (Number(payment?.amount) || 0);

export const sumVerifiedPayments = (payments = []) =>
  payments.reduce(
    (sum, payment) => (countsTowardPaid(payment) ? sum + signedPaymentAmount(payment) : sum),
    0
  );

/**
 * What has actually been received against an invoice, in the invoice's own currency.
 *
 * Prefers the payment rows: they carry each payment's verification state, and on a screen being
 * edited they are the only source that reflects a payment just added. Next comes the server's own
 * verifiedPaidAmount. Last is the stored summary, which needs both fields, since partialPaidAmount
 * holds a figure only while an invoice is partially paid: the backend clears it on full payment, so
 * a PAID invoice's paid amount is its whole total. Reading partialPaidAmount alone is what left a
 * paid invoice printing its full amount as still due.
 */
export const getInvoicePaidAmount = (item, total = getInvoiceGrossAmount(item)) => {
  const cap = (amount) => Math.min(Math.max(Number(amount) || 0, 0), Number(total) || 0);
  const payments = item?.payments;
  if (Array.isArray(payments) && payments.length) {
    return cap(sumVerifiedPayments(payments));
  }
  if (item?.verifiedPaidAmount != null) return cap(item.verifiedPaidAmount);
  const status = getPaymentStatus(item);
  if (status === PAYMENT_STATUS.PAID) return Number(total) || 0;
  if (status === PAYMENT_STATUS.PARTIALLY_PAID) return cap(item?.partialPaidAmount);
  return 0;
};

/** What is still owed once verified payments are taken off the invoice's total. */
export const getInvoiceBalanceDue = (item, total = getInvoiceGrossAmount(item)) =>
  Math.max((Number(total) || 0) - getInvoicePaidAmount(item, total), 0);

export const isOutstandingInvoice = (invoice) => {
  if (invoice?.lifecycleStatus === "DRAFT" || invoice?.lifecycleStatus === "PENDING_ACTIVATION")
    return false;
  const status = getPaymentStatus(invoice);
  return status === PAYMENT_STATUS.UNPAID || status === PAYMENT_STATUS.PARTIALLY_PAID;
};

export const getOutstandingInvoices = (invoices = []) =>
  invoices.filter(isOutstandingInvoice).sort((a, b) => {
    const aDate = new Date(a.issueDate || a.createdDate || 0).getTime();
    const bDate = new Date(b.issueDate || b.createdDate || 0).getTime();
    return bDate - aDate;
  });

export const paymentStatusLabel = (status) =>
  status === PAYMENT_STATUS.PAID
    ? "Paid"
    : status === PAYMENT_STATUS.PARTIALLY_PAID
    ? "Partially paid"
    : "Unpaid";
